import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Link } from "react-router-dom";
import { fetchMythicsData } from './db/dbMythics.js';
import { getCachedMythicsData, setCachedMythicsData } from './db/mythicsCache.js';
import './styles/Mythics.css';
import { useWallet } from './WalletContext.js';
import getConversionRates from './api/coingecko.js';

const TOTAL_MYTHICS_IN_COLLECTION = 60;

function Mythics() {
  const { walletAddress } = useWallet();
  const [mythicData, setMythicData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortColumn, setSortColumn] = useState('rank');
  const [sortDirection, setSortDirection] = useState('desc');
  const [conversionRates, setConversionRates] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let cached = getCachedMythicsData();
      let data;
      if (cached) {
        data = cached;
      } else {
        data = await fetchMythicsData();
        setCachedMythicsData(data);
      }
      const sortedNfts = sortData(data?.nfts || [], 'rank', 'desc', null);
      setMythicData(sortedNfts);
      // Warm the browser image cache so images are ready when rows render
      sortedNfts.forEach(m => { if (m.image_url) { new Image().src = m.image_url; } });
    } catch (err) {
      if (err?.code === 'permission-denied') {
        setError('Database permissions not configured yet. Update Firestore security rules and run the cron job from the admin panel.');
      } else {
        setError('Failed to load mythics. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchedRates = useRef(false);

  useEffect(() => {
    if (!fetchedRates.current) {
      const fetchRates = async () => {
        const rates = await getConversionRates();
        setConversionRates(rates);
        fetchedRates.current = true;
      };
      fetchRates();
    }
  }, []);

  useEffect(() => {
    if (conversionRates && mythicData.length > 0) {
      setMythicData(prev => sortData([...prev], sortColumn, sortDirection, conversionRates));
    }
  }, [conversionRates]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-compute owned counts once per wallet+data change (avoids per-cell recalculation)
  const ownedCountByRank = useMemo(() => {
    if (!walletAddress) return {};
    return Object.fromEntries(
      mythicData.map(m => [
        m.rank,
        (m.owners || []).filter(o => o?.toLowerCase() === walletAddress.toLowerCase()).length,
      ])
    );
  }, [mythicData, walletAddress]);

  const sortData = (data, column, direction, rates) => {
    return [...data].sort((a, b) => {
      if (column === 'floor' && rates) {
        return direction === 'desc'
          ? compareFloorValues(b.floor, a.floor, rates)
          : compareFloorValues(a.floor, b.floor, rates);
      } else if (column === 'rank' || column === 'minted') {
        return direction === 'desc' ? b[column] - a[column] : a[column] - b[column];
      } else if (column === 'name') {
        return direction === 'desc'
          ? b[column].localeCompare(a[column])
          : a[column].localeCompare(b[column]);
      } else if (column === 'owned') {
        const aOwned = ownedCountByRank[a.rank] ?? 0;
        const bOwned = ownedCountByRank[b.rank] ?? 0;
        return direction === 'desc' ? bOwned - aOwned : aOwned - bOwned;
      }
      return 0;
    });
  };

  const compareFloorValues = (floorA, floorB, rates) => {
    const parseAndConvert = (floor) => {
      if (!floor || floor === "-") return 0;
      const [value, currency] = floor.replace(/,/g, '').split(" ");
      const adjustedValue = parseFloat(value);
      const rateMap = {
        eth: rates.ethereum?.usd || 0,
        pol: rates["matic-network"]?.usd || 0,
        usdc: rates["usd-coin"]?.usd || 1,
      };
      return adjustedValue * (rateMap[currency?.toLowerCase()] || 0);
    };
    return parseAndConvert(floorA) - parseAndConvert(floorB);
  };

  const formatPrice = (value, currency) => {
    const adjustedValue = parseFloat(value);
    if (isNaN(adjustedValue)) return 'N/A';

    if (currency.toLowerCase() === "usdc") {
      return `${Math.round(adjustedValue).toFixed(0)} ${currency.toUpperCase()}`;
    }

    const isInteger = Number.isInteger(adjustedValue);
    return `${adjustedValue.toFixed(isInteger ? 0 : 2)} ${currency.toUpperCase()}`;
  };

  const handleSort = (newColumn) => {
    const newDirection = sortColumn === newColumn && sortDirection === 'desc' ? 'asc' : 'desc';
    setSortColumn(newColumn);
    setSortDirection(newDirection);
    setMythicData(prev => sortData([...prev], newColumn, newDirection, conversionRates));
  };

  const getSortIndicator = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const buildOpenSeaLink = (mythicFilterName) => {
    const traits = encodeURIComponent(JSON.stringify([
      { traitType: "Collection", values: ["Mythics"] },
      { traitType: "Mythic", values: [mythicFilterName] },
    ]));
    return `https://opensea.io/collection/yellow-ducklings?search[sortAscending]=false&search[sortBy]=CREATED_DATE&search[stringTraits][0][name]=Collection&search[stringTraits][0][values][0]=Mythics&traits=${traits}&sortBy=created_date&sortDirection=desc`;
  };

  const createMintedLink = (mythic) => buildOpenSeaLink(mythic.mythicTrait || mythic.name);

  const createOwnedLink = (account, mythic) => {
    const filterName = mythic.mythicTrait || mythic.name;
    const traits = encodeURIComponent(JSON.stringify([
      { traitType: "Collection", values: ["Mythics"] },
      { traitType: "Mythic", values: [filterName] },
    ]));
    return `https://opensea.io/collection/yellow-ducklings?owner=${account}&traits=${traits}&sortBy=created_date&sortDirection=desc`;
  };

  // Memoized filtered list — only recomputed when data or search term changes
  const displayData = useMemo(() =>
    searchTerm
      ? mythicData.filter(m => m.name.toLowerCase().includes(searchTerm.toLowerCase()))
      : mythicData,
    [mythicData, searchTerm]
  );

  const revealSummary = useMemo(() => {
    const mintedCount = mythicData.filter(m => Number(m.minted || 0) > 0).length;
    const unrevealed = Math.max(TOTAL_MYTHICS_IN_COLLECTION - mintedCount, 0);
    const percent = Math.round((mintedCount / TOTAL_MYTHICS_IN_COLLECTION) * 100);
    return { mintedCount, unrevealed, percent };
  }, [mythicData]);

  if (error) {
    return (
      <div className="error-message">
        <div className="error-message-card">
          <p>{error}</p>
          <button onClick={loadData}>Retry</button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mythics-container">
        <table>
          <thead>
            <tr>
              <th>Rank</th>
              <th></th>
              <th>Name</th>
              <th>Minted</th>
              <th>Floor</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 12 }).map((_, i) => (
              <tr key={i}>
                <td><span className="skeleton" style={{ width: 28, height: 18 }} /></td>
                <td><span className="skeleton" style={{ width: 64, height: 64, borderRadius: 6 }} /></td>
                <td><span className="skeleton" style={{ width: 120, height: 16 }} /></td>
                <td><span className="skeleton" style={{ width: 30, height: 30, borderRadius: '50%' }} /></td>
                <td><span className="skeleton" style={{ width: 60, height: 16 }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="mythics-container">
      <>
        <section className="mythics-notice page-notice-card" aria-label="Collection reveal status">
          <div className="mythics-notice-main">
            <h3>Collection Reveal Status</h3>
            <p>
              <strong>{revealSummary.mintedCount}</strong> / {TOTAL_MYTHICS_IN_COLLECTION} Mythics revealed
              <span className="mythics-notice-sep">•</span>
              <strong>{revealSummary.unrevealed}</strong> unrevealed
            </p>
          </div>
          <div className="mythics-notice-progress">
            <div
              className="mythics-notice-progress-fill"
              style={{ width: `${revealSummary.percent}%` }}
            />
          </div>
          <small>{revealSummary.percent}% of the full Mythic collection is revealed.</small>
        </section>
        <table>
          <thead>
            <tr className="search-row">
              <th colSpan={walletAddress ? 6 : 5} className="search-th">
                <input
                  type="text"
                  placeholder="Search mythics..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="table-search-input"
                  aria-label="Search mythics"
                />
                {searchTerm ? (
                  <button
                    className="table-search-clear"
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                  >✕</button>
                ) : (
                  <span className="table-search-icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                  </span>
                )}
              </th>
            </tr>
            <tr>
              <th onClick={() => handleSort('rank')}>Rank {getSortIndicator('rank')}</th>
              <th style={{ cursor: 'default' }}></th>
              <th onClick={() => handleSort('name')}>Name {getSortIndicator('name')}</th>
              <th onClick={() => handleSort('minted')}>Minted {getSortIndicator('minted')}</th>
              {walletAddress && <th onClick={() => handleSort('owned')}>Owned {getSortIndicator('owned')}</th>}
              <th onClick={() => handleSort('floor')}>Floor {getSortIndicator('floor')}</th>
            </tr>
          </thead>
          <tbody>
            {displayData.length === 0 && searchTerm
              ? (
                <tr>
                  <td colSpan={walletAddress ? 6 : 5} style={{ textAlign: 'center', padding: '32px', opacity: 0.6 }}>
                    No mythics match &ldquo;{searchTerm}&rdquo;
                  </td>
                </tr>
              )
              : displayData.map((mythic) => {
              const ownedCount = ownedCountByRank[mythic.rank] ?? 0;
              return (
                <tr
                  key={mythic.rank}
                  className={
                    walletAddress && ownedCount > 0
                      ? 'highlight'
                      : walletAddress
                      ? 'not-yet'
                      : ''
                  }
                >
                  <td className="mythics-rank">{mythic.rank}</td>
                  <td className="mythics-image">
                    <Link to={`/mythics/${mythic.rank}`}>
                      <img src={mythic.image_url} alt={mythic.name} loading="lazy" />
                    </Link>
                  </td>
                  <td className="mythics-name">
                    <Link to={`/mythics/${mythic.rank}`}>{mythic.name}</Link>
                  </td>
                  <td className="mythics-minted">
                    <a href={createMintedLink(mythic)} target="_blank" rel="noopener noreferrer">
                      <span>{mythic.minted}</span>
                    </a>
                  </td>
                  {walletAddress && (
                    <td className={`mythics-owned ${ownedCount > 0 ? 'highlight' : 'not-yet'}`}>
                      {ownedCount > 0 ? (
                        <a href={createOwnedLink(walletAddress, mythic)} target="_blank" rel="noopener noreferrer">
                          {ownedCount}
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                  )}
                  <td className="mythics-floor">
                    {mythic.floor && mythic.floor !== "-" ? (() => {
                      const parts = mythic.floor.split(" ");
                      if (parts.length === 2) {
                        const [value, currency] = parts;
                        return formatPrice(value, currency);
                      }
                      return 'N/A';
                    })() : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </>
    </div>
  );
}

export default Mythics;
